import { useEffect, useRef, useState } from 'react';

export interface AIQAPair {
  q: string;
  a: string;
}

interface AIAnswerBlockProps {
  items: AIQAPair[];
  heading?: string;
}

export default function AIAnswerBlock({
  heading = 'About Expert Coating Service',
  items,
}: AIAnswerBlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#0C0C0C] py-24 md:py-32 px-6 md:px-12 lg:px-16 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-[#C9A063]/4 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div
          className={`flex flex-col lg:flex-row gap-16 items-start transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="lg:w-[300px] shrink-0">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[1px] bg-[#C9A063]" />
              <span className="text-[#C9A063] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">AI Optimized</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-[1.1] mb-5">{heading}</h2>
            <p className="text-white/40 text-sm font-sans leading-relaxed">
              Structured answers designed for AI search engines, voice queries, and featured snippet extraction.
            </p>
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item, i) => (
              <div
                key={i}
                className={`bg-[#141414] border border-white/6 rounded-2xl p-7 hover:border-[#C9A063]/20 transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 rounded-full bg-[#C9A063]/15">
                    <i className="ri-question-mark text-[#C9A063] text-[11px] font-bold"></i>
                  </div>
                  <strong className="font-sans text-white/80 text-sm font-semibold leading-snug">{item.q}</strong>
                </div>
                <div className="pl-7">
                  <p className="text-white/45 font-sans text-sm leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
